import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, Plus, Star, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatCEP, lookupCEP } from "@/lib/format";
import {
  myAddressesQuery,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
  type CustomerAddress,
  type AddressInput,
} from "@/lib/addresses.functions";

export const Route = createFileRoute("/minha-conta/enderecos")({
  head: () => ({
    meta: [{ title: "Meus Endereços — Loja EG Elétrica" }, { name: "robots", content: "noindex" }],
  }),
  component: EnderecosPage,
});

const emptyForm: AddressInput = {
  label: "",
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
};

function EnderecosPage() {
  const user = useAuth((s) => s.user);
  const initialized = useAuth((s) => s.initialized);
  const queryClient = useQueryClient();
  const { data: addresses, isLoading } = useQuery({ ...myAddressesQuery(), enabled: !!user });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AddressInput>(emptyForm);
  const [cepLoading, setCepLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actioningId, setActioningId] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["my-addresses"] });

  const handleSetDefault = async (id: string) => {
    setActioningId(id);
    setActionError("");
    try {
      await setDefaultAddress({ data: id });
      invalidate();
    } catch {
      setActionError("Não foi possível tornar este endereço o padrão. Tente novamente.");
    } finally {
      setActioningId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Excluir este endereço?")) return;
    setActioningId(id);
    setActionError("");
    try {
      await deleteAddress({ data: id });
      invalidate();
    } catch {
      setActionError("Não foi possível excluir este endereço. Tente novamente.");
    } finally {
      setActioningId(null);
    }
  };

  const set = (k: keyof AddressInput, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const startCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (addr: CustomerAddress) => {
    setForm({
      label: addr.label ?? "",
      cep: addr.cep,
      logradouro: addr.logradouro,
      numero: addr.numero,
      complemento: addr.complemento,
      bairro: addr.bairro,
      cidade: addr.cidade,
      uf: addr.uf,
    });
    setEditingId(addr.id);
    setShowForm(true);
  };

  const handleCEPBlur = async () => {
    const clean = form.cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading(true);
    const addr = await lookupCEP(clean);
    setCepLoading(false);
    if (!addr) return;
    setForm((f) => ({
      ...f,
      logradouro: addr.logradouro || f.logradouro,
      bairro: addr.bairro || f.bairro,
      cidade: addr.localidade || f.cidade,
      uf: addr.uf || f.uf,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (editingId) {
        await updateAddress({ data: { ...form, id: editingId } });
      } else {
        await createAddress({ data: form });
      }
      invalidate();
      setShowForm(false);
    } catch {
      setError("Não foi possível salvar o endereço. Confira os campos.");
    } finally {
      setSubmitting(false);
    }
  };

  if (initialized && !user) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">Meus Endereços</h1>
        <p className="mb-4 text-sm text-muted-foreground">
          Entre na sua conta para gerenciar seus endereços.
        </p>
        <Link to="/login" className="text-sm font-medium text-eg-goldtext hover:underline">
          Entrar
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-eg-dark">Meus Endereços</h1>
        {!showForm && (
          <button
            type="button"
            onClick={startCreate}
            className="inline-flex items-center gap-2 rounded-md bg-eg-gold px-4 py-2 text-sm font-semibold text-eg-dark hover:bg-eg-goldhover transition-colors"
          >
            <Plus className="h-4 w-4" />
            Novo endereço
          </button>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-eg-gold" />
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 space-y-4 rounded-lg border border-border bg-white p-6"
        >
          <h2 className="font-display font-bold text-eg-dark">
            {editingId ? "Editar endereço" : "Novo endereço"}
          </h2>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-eg-dark">Apelido (opcional)</span>
            <input
              className="input"
              placeholder="Casa, Trabalho..."
              value={form.label}
              onChange={(e) => set("label", e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-eg-dark">CEP</span>
            <div className="relative">
              <input
                required
                minLength={9}
                className="input"
                value={form.cep}
                placeholder="00000-000"
                onChange={(e) => set("cep", formatCEP(e.target.value))}
                onBlur={handleCEPBlur}
              />
              {cepLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-eg-dark">Logradouro</span>
            <input
              required
              className="input"
              value={form.logradouro}
              onChange={(e) => set("logradouro", e.target.value)}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-eg-dark">Número</span>
              <input
                required
                className="input"
                value={form.numero}
                onChange={(e) => set("numero", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-eg-dark">Complemento</span>
              <input
                className="input"
                value={form.complemento}
                onChange={(e) => set("complemento", e.target.value)}
              />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-eg-dark">Bairro</span>
            <input
              required
              className="input"
              value={form.bairro}
              onChange={(e) => set("bairro", e.target.value)}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-[1fr_100px]">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-eg-dark">Cidade</span>
              <input
                required
                className="input"
                value={form.cidade}
                onChange={(e) => set("cidade", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-eg-dark">UF</span>
              <input
                required
                maxLength={2}
                className="input uppercase"
                value={form.uf}
                onChange={(e) => set("uf", e.target.value.toUpperCase())}
              />
            </label>
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-eg-gold px-6 py-2 text-sm font-semibold text-eg-dark hover:bg-eg-goldhover disabled:opacity-60 transition-colors"
            >
              {submitting ? "Salvando..." : "Salvar"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-eg-dark hover:bg-muted transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {actionError && <p className="mb-3 text-xs text-red-600">{actionError}</p>}

      <div className="space-y-3">
        {addresses?.map((addr) => (
          <div key={addr.id} className="rounded-lg border border-border bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="text-sm">
                  {addr.label && <p className="font-semibold text-eg-dark">{addr.label}</p>}
                  <p className="text-eg-dark">
                    {addr.logradouro}, {addr.numero}
                    {addr.complemento ? ` — ${addr.complemento}` : ""}
                  </p>
                  <p className="text-muted-foreground">
                    {addr.bairro}, {addr.cidade}/{addr.uf} — {addr.cep}
                  </p>
                  {addr.is_default && (
                    <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-eg-goldtext">
                      <Star className="h-3 w-3 fill-eg-gold text-eg-gold" />
                      Padrão
                    </span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 text-xs">
                {actioningId === addr.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {!addr.is_default && (
                  <button
                    type="button"
                    onClick={() => handleSetDefault(addr.id)}
                    disabled={actioningId === addr.id}
                    className="font-medium text-eg-goldtext hover:underline disabled:opacity-50"
                  >
                    Tornar padrão
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => startEdit(addr)}
                  aria-label="Editar"
                  className="text-muted-foreground hover:text-eg-dark"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(addr.id)}
                  disabled={actioningId === addr.id}
                  aria-label="Excluir"
                  className="text-muted-foreground hover:text-red-600 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {!isLoading && addresses?.length === 0 && !showForm && (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Nenhum endereço salvo ainda.
          </p>
        )}
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background: white;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          border-color: var(--eg-gold);
          box-shadow: 0 0 0 2px color-mix(in oklab, var(--eg-gold) 30%, transparent);
        }
      `}</style>
    </div>
  );
}
